import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Disc, Sparkles } from 'lucide-react';
import { API_URL } from '../../../config';
import CountryFlag from '../../../components/countryFlags/countryFlag';
import InvestorLogo from '../../../components/data/InvestorLogo';
import Pagination from '../../../components/ui/pagination';
import { buildInvestorProfilePath, getInvestorTypes } from '../../../components/investor-display/helpers';
import { useCRM } from '../../../hooks/crm';

const ITEMS_PER_PAGE = 4;

const RecommendedInvestorsList = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { boards, loading: crmLoading } = useCRM();
  const [recommendedInvestors, setRecommendedInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const generateLink = useCallback((investor) => {
    const path = buildInvestorProfilePath({
      investor,
      language: i18n.language,
      inApp: true,
    });

    return `${path}${location.search || ''}`;
  }, [i18n.language, location.search]);

  useEffect(() => {
    const fetchRecommendedInvestors = async () => {
      if (crmLoading) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const investorIdsInBoards = [];
        boards.forEach((board) => {
          board.columns?.forEach((col) => {
            col.investors?.forEach((inv) => {
              if (inv?.investor_id) investorIdsInBoards.push(inv.investor_id);
            });
          });
        });
        const excludeParam = investorIdsInBoards.join(',');

        const { data } = await axios.get(
          `${API_URL}/recommended-investors${excludeParam ? `?exclude=${excludeParam}` : ''}`,
          { withCredentials: true }
        );
        setRecommendedInvestors(data?.investors || []);
        setCurrentPage(1);
      } catch (err) {
        if (err?.response?.status === 404) {
          setRecommendedInvestors([]);
          setError(null);
        } else {
          setError(t('app:dashboard.recommended.error'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendedInvestors();
  }, [boards, crmLoading, t]);

  const totalPages = Math.ceil(recommendedInvestors.length / ITEMS_PER_PAGE);
  const currentInvestors = recommendedInvestors.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) {
    return (
      <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-stone-500">{t('app:dashboard.recommended.loading')}</p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
            <Sparkles className="h-3.5 w-3.5" />
            {t('app:dashboard.recommended.badge')}
          </div>
          <h3 className="text-xl font-semibold tracking-tight text-stone-950">
            {t('app:dashboard.t6')}
          </h3>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            {t('app:dashboard.recommended.subtitle')}
          </p>
        </div>
        <div className="inline-flex h-8 shrink-0 items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 shadow-sm">
          <Disc className="h-3.5 w-3.5" />
          <span>{t('app:dashboard.recommended.count', { count: recommendedInvestors.length })}</span>
        </div>
      </div>

      {error || recommendedInvestors.length === 0 ? (
        <div className="mt-5 rounded-lg border border-dashed border-stone-300 bg-stone-50/70 px-4 py-3 text-sm leading-6 text-stone-600">
          {error || t('app:dashboard.t7')}
        </div>
      ) : (
        <>
          <ul className="mt-5 space-y-3">
            {currentInvestors.map((investor) => {
              const nameText = investor?.name || '—';
              const investorTypes = getInvestorTypes(investor);
              const industries = Array.isArray(investor?.industries)
                ? investor.industries.filter(Boolean)
                : [];

              return (
                <li
                  key={investor._id}
                  className="rounded-lg border border-stone-200 bg-stone-50/70 p-4"
                >
                  <div className="flex items-start gap-3">
                    <InvestorLogo
                      id={investor?.id}
                      country={investor?.country}
                      className="h-9 w-9 flex-none"
                      alt={`${nameText} logo`}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <Link
                            to={generateLink(investor)}
                            className="group inline-flex max-w-full items-center gap-1 text-sm font-semibold leading-5 text-stone-950 hover:underline"
                          >
                            <span className="truncate">{nameText}</span>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                          </Link>

                          {investorTypes.length > 0 ? (
                            <div className="mt-1 text-xs text-stone-500">
                              {investorTypes.slice(0, 2).join(' / ')}
                            </div>
                          ) : null}
                        </div>

                        {investor.country ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-stone-600 ring-1 ring-stone-200">
                            <CountryFlag countryName={investor.country} className="h-3 w-5" />
                            <span>{investor.country}</span>
                          </span>
                        ) : null}
                      </div>

                      {industries.length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {industries.slice(0, 3).map((industry) => (
                            <span
                              key={`${investor._id}-${industry}`}
                              className="inline-flex max-w-full items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-600 ring-1 ring-stone-200"
                            >
                              <span className="truncate">{industry}</span>
                            </span>
                          ))}
                          {industries.length > 3 ? (
                            <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-500 ring-1 ring-stone-200">
                              {t('app:dashboard.recommended.moreIndustries', { count: industries.length - 3 })}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {totalPages > 1 ? (
            <div className="mt-4 flex justify-start">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default RecommendedInvestorsList;
